// Patient education library — blog-style articles with rich medical content.

export const ARTICLES = [
    {
        slug: "understanding-colonoscopy",
        title: "Understanding Colonoscopy: What to Expect and Why It Matters",
        category: "Procedures",
        read_time: 6,
        updated: "March 2025",
        excerpt:
            "Colonoscopy is one of the most effective cancer-prevention tools in modern medicine. Here's a clear, calm walkthrough of what happens — and why it matters.",
        body: [
            { type: "p", text: "Colonoscopy allows a physician to examine the entire lining of the colon and rectum using a slim, flexible instrument with a high-definition camera. It's the only screening test that can both detect AND prevent colon cancer, because polyps found during the exam are typically removed in the same session." },
            { type: "h2", text: "Why is it recommended?" },
            { type: "p", text: "Most colon cancers begin as small, non-cancerous polyps that grow slowly over many years. Removing them before they can transform is the single most powerful tool in colorectal cancer prevention." },
            { type: "h2", text: "When should I have one?" },
            { type: "ul", items: [
                "Average-risk adults: starting at age 45",
                "Family history of colon cancer or advanced polyps: often earlier and more frequently",
                "Hereditary syndromes (Lynch, FAP): individualized surveillance schedules",
                "Any red-flag symptoms (bleeding, unexplained anemia, weight loss): regardless of age",
            ]},
            { type: "h2", text: "What does prep involve?" },
            { type: "p", text: "You'll follow a clear-liquid diet the day before and drink a prescribed bowel-cleansing solution in two split doses. Modern preps are far more tolerable than they used to be. Our clinic provides step-by-step instructions and support." },
            { type: "h2", text: "What happens on procedure day?" },
            { type: "p", text: "You'll receive gentle sedation. The exam itself is typically 20–40 minutes. You will not remember the procedure, and any polyps identified are usually removed painlessly. Most patients are back to normal activities the same or next day." },
            { type: "h2", text: "What are the risks?" },
            { type: "p", text: "Colonoscopy is extremely safe. Rare risks include bleeding or perforation — the overall risk of a serious complication is less than 1 in 1,000 for screening colonoscopy in experienced hands." },
            { type: "h2", text: "Bottom line" },
            { type: "p", text: "Colonoscopy saves lives. If you're overdue for screening, or have symptoms that concern you, we'd be glad to help you take the next step — clearly and comfortably." },
        ],
    },
    {
        slug: "what-is-gerd",
        title: "What Is GERD? Beyond Occasional Heartburn",
        category: "Conditions",
        read_time: 5,
        updated: "February 2025",
        excerpt:
            "Everyone gets heartburn now and then, but persistent reflux is different. Learn how to tell the difference and when to seek help.",
        body: [
            { type: "p", text: "Gastroesophageal reflux disease (GERD) is a condition where stomach contents repeatedly move back into the esophagus. Occasional heartburn after a big meal is normal; frequent or severe symptoms are not, and can lead to esophageal damage over time." },
            { type: "h2", text: "Common symptoms" },
            { type: "ul", items: [
                "Burning in the chest, especially after meals or lying down",
                "Regurgitation of food or acid",
                "Chronic cough, hoarseness, or a lump-in-throat sensation",
                "Difficulty or pain with swallowing (this deserves prompt evaluation)",
            ]},
            { type: "h2", text: "When lifestyle isn't enough" },
            { type: "p", text: "Weight, meal timing, and sleep positioning can meaningfully reduce symptoms. If those aren't enough, medications and — in select cases — endoscopic evaluation or antireflux procedures may be warranted." },
            { type: "h2", text: "When to see a specialist" },
            { type: "p", text: "See a gastroenterologist if you have symptoms more than twice a week, difficulty swallowing, unintentional weight loss, blood in vomit or stool, or if you've been on acid-suppressing medications for years without a plan." },
        ],
    },
    {
        slug: "signs-see-gastroenterologist",
        title: "Signs You Should See a Gastroenterologist",
        category: "Wellness",
        read_time: 4,
        updated: "April 2025",
        excerpt:
            "Many digestive symptoms are common — but some deserve a specialist's evaluation. Here are the signs not to ignore.",
        body: [
            { type: "h2", text: "Red-flag symptoms" },
            { type: "ul", items: [
                "Blood in the stool or black, tarry stools",
                "Unintentional weight loss",
                "New difficulty or pain with swallowing",
                "Persistent vomiting",
                "Iron-deficiency anemia without a clear cause",
                "Family history of colon cancer or IBD",
            ]},
            { type: "h2", text: "Chronic symptoms worth evaluating" },
            { type: "ul", items: [
                "Persistent heartburn or reflux (more than twice a week)",
                "Chronic diarrhea or constipation",
                "Recurring abdominal pain or bloating",
                "Long-term acid-suppression medication without a documented plan",
            ]},
            { type: "p", text: "A specialist visit doesn't always mean a procedure. Sometimes the most valuable outcome is clarity: a clear explanation of what's happening, what's not, and what — if anything — to do next." },
        ],
    },
    {
        slug: "foods-digestive-health",
        title: "Foods That Support Digestive Health",
        category: "Nutrition",
        read_time: 5,
        updated: "May 2025",
        excerpt:
            "Nutrition is one of your most powerful tools for digestive wellness. Here's what the evidence actually supports.",
        body: [
            { type: "h2", text: "Fiber — the foundation" },
            { type: "p", text: "Fiber supports regular bowel habits, feeds a healthy microbiome, and is associated with lower risks of many digestive conditions. Aim for 25–35 grams per day from a variety of sources: whole grains, legumes, fruits, and vegetables." },
            { type: "h2", text: "Fermented foods (in moderation)" },
            { type: "p", text: "Yogurt, kefir, sauerkraut, and kimchi contribute beneficial microbes. They're a nice addition — but not a magic bullet." },
            { type: "h2", text: "Healthy fats" },
            { type: "p", text: "Olive oil, avocado, nuts, and fatty fish support metabolic and liver health." },
            { type: "h2", text: "What to limit" },
            { type: "ul", items: [
                "Ultra-processed foods and refined sugars",
                "Excess alcohol",
                "Sugar-sweetened beverages",
                "Excess red and processed meat, particularly for colon health",
            ]},
            { type: "p", text: "Every digestive condition has nuances. If you have IBS, IBD, GERD, or celiac disease, dietary strategies should be personalized — not guessed from the internet." },
        ],
    },
    {
        slug: "understanding-ibs",
        title: "Understanding IBS: Real Condition, Real Solutions",
        category: "Conditions",
        read_time: 6,
        updated: "February 2025",
        excerpt:
            "IBS is a real, common, and highly treatable condition — not just 'in your head.' Here's what modern management looks like.",
        body: [
            { type: "p", text: "Irritable bowel syndrome affects an estimated 10–15% of adults worldwide. It's characterized by recurrent abdominal pain associated with changes in bowel habits, without structural disease." },
            { type: "h2", text: "Subtypes matter" },
            { type: "ul", items: [
                "IBS-D: diarrhea-predominant",
                "IBS-C: constipation-predominant",
                "IBS-M: mixed pattern",
            ]},
            { type: "h2", text: "Modern management" },
            { type: "p", text: "A tailored plan may include dietary strategies (including low-FODMAP when appropriate), targeted medications, and gut-directed cognitive therapies. The best plans meet you where you are and evolve with your response." },
            { type: "h2", text: "What is NOT IBS?" },
            { type: "p", text: "Red-flag features — bleeding, unintentional weight loss, symptoms waking you at night, family history of colon cancer or IBD — always deserve further evaluation before settling on an IBS diagnosis." },
        ],
    },
    {
        slug: "crohns-explained",
        title: "Crohn's Disease Explained: Diagnosis, Treatment, Life Ahead",
        category: "Conditions",
        read_time: 7,
        updated: "March 2025",
        excerpt:
            "Crohn's disease is manageable with modern therapies. Here's what a comprehensive care plan actually looks like.",
        body: [
            { type: "h2", text: "What is Crohn's?" },
            { type: "p", text: "Crohn's is a chronic, immune-mediated inflammation that can affect any part of the digestive tract, most commonly the last part of the small intestine and the beginning of the colon. Its course varies widely from person to person." },
            { type: "h2", text: "How is it diagnosed?" },
            { type: "p", text: "Diagnosis is based on a combination of symptoms, blood and stool markers, endoscopy with biopsies, and cross-sectional imaging like MR enterography." },
            { type: "h2", text: "Modern treatments" },
            { type: "p", text: "Most patients today are treated with biologic or small-molecule therapies that target specific inflammatory pathways. Choosing the right one depends on disease location, severity, prior treatments, and personal factors like family planning." },
            { type: "h2", text: "Living well with Crohn's" },
            { type: "ul", items: [
                "Structured monitoring — even in remission",
                "Vaccination review and preventive health",
                "Bone-health and nutrition support",
                "Mental health resources when needed",
            ]},
        ],
    },
    {
        slug: "colon-cancer-screening-guidelines",
        title: "Colon Cancer Screening: Current Guidelines Explained",
        category: "Prevention",
        read_time: 5,
        updated: "January 2025",
        excerpt:
            "Screening guidelines have evolved. Here's what current recommendations say — and how to choose the right test for you.",
        body: [
            { type: "h2", text: "Average risk" },
            { type: "p", text: "For average-risk adults, colon cancer screening is now recommended starting at age 45 (previously 50). Options include colonoscopy, the FIT (Fecal Immunochemical Test) done annually, and CT colonography." },
            { type: "h2", text: "Higher risk" },
            { type: "p", text: "If you have a first-degree relative with colon cancer or advanced polyps, screening usually starts earlier — age 40 or 10 years before the youngest relative's diagnosis, whichever comes first." },
            { type: "h2", text: "Which test is right for me?" },
            { type: "ul", items: [
                "Colonoscopy: gold standard — detects AND removes polyps in one visit",
                "FIT: simple, at-home, annual; positive tests always need colonoscopy follow-up",
                "CT colonography: imaging-based alternative when colonoscopy isn't feasible; abnormal findings require follow-up colonoscopy",
            ]},
            { type: "p", text: "The best screening test is the one you'll actually do. We can help you decide together based on your history and preferences." },
        ],
    },
    {
        slug: "digestive-myths-facts",
        title: "Digestive Health Myths vs Facts",
        category: "Wellness",
        read_time: 4,
        updated: "April 2025",
        excerpt:
            "The internet is loud on digestive health. Here are the myths we hear most often — and what the evidence actually says.",
        body: [
            { type: "h2", text: "Myth: You need to 'detox' your gut with cleanses" },
            { type: "p", text: "Your liver and kidneys are extraordinary detox organs. Colon cleanses have no proven benefit and can cause harm." },
            { type: "h2", text: "Myth: Nuts and seeds cause diverticulitis" },
            { type: "p", text: "Modern evidence has debunked this myth. Nuts, seeds, and popcorn are safe for most patients with diverticulosis." },
            { type: "h2", text: "Myth: Everyone should be gluten-free" },
            { type: "p", text: "Only patients with celiac disease, wheat allergy, or documented non-celiac gluten sensitivity benefit from strict gluten avoidance. Test before you eliminate." },
            { type: "h2", text: "Fact: Screening truly prevents colon cancer" },
            { type: "p", text: "This one is not a myth. Screening saves lives. If you're due, don't put it off." },
        ],
    },
];

export function getArticleBySlug(slug) {
    return ARTICLES.find((a) => a.slug === slug);
}

export const ARTICLE_CATEGORIES = ["All", "Procedures", "Conditions", "Nutrition", "Prevention", "Wellness"];
